import dayjs from 'dayjs';
import {TrendingFlat} from '@mui/icons-material';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TimedMsgRequestTimesCard, {
  iTimedMsgTimesItem,
} from './TimedMsgRequestTimesCard';
import IconWarning from '../../../../../asset/icons/icon_warning.svg';
import IconChecked from '../../../../../asset/icons/icon_checked.svg';
import {ITimedDuplicateTimes, iTimedMsgDateRange} from '../TimedMessageRequest';
import {isAllDaysTimed} from '../../../../../components/utlis/date-time/GetDaysInDateRange';
import TimedMsgRequestReviewDayTimes from '../review/TimedMsgRequestReviewDayTimes';
import {ITimedMessageDayType} from '../../../../../dto/timed-message/TimedMessageRequest.dto';

type Props = {
  isAddSlot: boolean;
  items: iTimedMsgTimesItem[];
  selectedWeekdays: string[];
  enableWeekdays: boolean;
  setDaysTimes: React.Dispatch<React.SetStateAction<iTimedMsgTimesItem[]>>;
  availableDays: string[];
  duplicateTimes?: ITimedDuplicateTimes[];
  dayType: ITimedMessageDayType;
};

const TimedMsgRequestTimes = ({
  isAddSlot,
  items,
  selectedWeekdays,
  enableWeekdays,
  setDaysTimes,
  availableDays,
  duplicateTimes,
  dayType,
}: Props) => {
  const handleRemove = useCallback(
    (id: number) => {
      setDaysTimes(prevItems => {
        const index = prevItems.findIndex(o => o.id === id);
        if (index === -1) return prevItems;
        const newItems = [...prevItems];
        newItems.splice(index, 1);
        return newItems;
      });
    },
    [setDaysTimes],
  );

  const handleAddSlot = useCallback(() => {
    setDaysTimes(prevItems => [
      ...prevItems,
      {
        id: Date.now(),
        selectedDays: [],
        timeRanges: [],
      },
    ]);
  }, [setDaysTimes]);

  return (
    <>
      <Box className='flex justify-between items-start h-40'>
        <Box>
          <Typography variant='h5'>Select Time</Typography>
          <Typography
            variant='subtitle1'
            className='text-white-50 text-14 italic'></Typography>
        </Box>
        {isAddSlot && (
          <Button
            startIcon={<AddIcon />}
            variant='outlined'
            size='small'
            onClick={handleAddSlot}>
            Add Slot
          </Button>
        )}
      </Box>

      <Stack
        direction='row'
        spacing={2}
        className='pr-2 mt-3 w-full h-full overflow-x scrollbar'>
        {items.map((item, index) => (
          <TimedMsgRequestTimesCard
            key={item.id}
            title={enableWeekdays ? `Slot ${index + 1}` : ``}
            item={item}
            enableWeekdays={enableWeekdays}
            selectedWeekdays={selectedWeekdays}
            removeBtnDisabled={items.length === 1}
            setDaysTimes={setDaysTimes}
            onRemove={handleRemove}
            availableDays={availableDays}
            duplicateTimes={duplicateTimes}
            dayType={dayType}
          />
        ))}
      </Stack>
    </>
  );
};

export default TimedMsgRequestTimes;

export const MultipleDateSelectTimeCmpt = ({
  dateRanges,
  setDateRanges,
  updateId,
  defaultDateRanges,
  duplicateTimes,
  dayType,
}: {
  dateRanges: iTimedMsgDateRange[];
  setDateRanges: React.Dispatch<React.SetStateAction<iTimedMsgDateRange[]>>;
  updateId?: number;
  defaultDateRanges: iTimedMsgDateRange[];
  duplicateTimes: ITimedDuplicateTimes[];
  dayType: ITimedMessageDayType;
}) => {
  const [page, setPage] = useState(1);
  const [daysTimes, setDaysTimes] = useState<iTimedMsgTimesItem[]>([]);
  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{
    start_date: dayjs.Dayjs | null;
    end_date: dayjs.Dayjs | null;
  }>({start_date: null, end_date: null});
  const [selectedWeekdays, setSelectedWeekdays] = useState<string[]>([]);
  const [dateRangeIds, setDateRangeIds] = useState<number>(0);
  const [currentDateRange, setCurrentDateRange] =
    useState<iTimedMsgDateRange | null>(null);

  const isAllDaysSelected = useMemo(
    () => (currentDateRange ? isAllDaysTimed(currentDateRange) : false),
    [currentDateRange],
  );

  const previousDaysTimes = useMemo(() => {
    if (updateId) {
      const matchedRange = defaultDateRanges.find(
        val => val.id === dateRangeIds,
      );
      return matchedRange ? matchedRange.day_times : [];
    }
    return [];
  }, [updateId, dateRangeIds, defaultDateRanges]);

  useEffect(() => {
    const flattenedWeeks = daysTimes.map(o => o.selectedDays).flat();
    const uniqueWeeks = flattenedWeeks.filter(
      (item, index, array) => array.indexOf(item) === index,
    );
    setSelectedWeekdays([...uniqueWeeks]);
  }, [daysTimes]);

  const handleRemove = useCallback(
    (id: number) => {
      setDaysTimes(prevItems => {
        const index = prevItems.findIndex(o => o.id === id);
        if (index === -1) return prevItems;
        const newItems = [...prevItems];
        newItems.splice(index, 1);
        return newItems;
      });
    },
    [setDaysTimes],
  );

  const handleChange = useCallback(
    (_event: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
      setDaysTimes([]);
      setDateRange({start_date: null, end_date: null});
    },
    [],
  );

  const handleAddSlot = useCallback(() => {
    setDaysTimes(prevItems => {
      return [
        ...prevItems,
        {
          id: Date.now(),
          availableDays: prevItems[0]
            ? (prevItems[0].availableDays as string[])
            : [],
          selectedDays: [],
          timeRanges: [],
        },
      ];
    });
  }, [setDaysTimes]);

  // When page changes, always initialize daysTimes from dateRanges[page-1].day_times
  useEffect(() => {
    setDateRanges(prev => {
      return prev.map((val, index) => {
        if (index === page - 1) {
          setDateRangeIds(val.id);
          if (daysTimes.length === 0) {
            setDaysTimes(val.day_times ?? []);
            setAvailableDays(val.chosenDays ?? []);
          }
          setDateRange({start_date: val.start_date, end_date: val.end_date});
          setCurrentDateRange(val);
        }
        return val;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDateRanges, page]);

  // When daysTimes changes, always update dateRanges[page-1].day_times
  useEffect(() => {
    setDateRanges(prev => {
      return prev.map((val, index) => {
        if (index === page - 1 && daysTimes.length) {
          const newVal = {...val, day_times: daysTimes};
          setCurrentDateRange(newVal);
          return newVal;
        }
        return val;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysTimes]);

  return (
    <Grid container spacing={1} className='h-full w-full'>
      <Grid
        item
        md={updateId && previousDaysTimes ? 9 : 12}
        className='h-full w-full'>
        <Stack direction='row' spacing={1} className='h-100'>
          <Box
            className='w-430 pr-3 border-right-1 h-full overflow-auto scrollbar'
            sx={{position: 'relative'}}>
            <Stack className='h-full'>
              <Typography variant='h5'>Select Time</Typography>
              <Typography
                variant='subtitle1'
                className='text-white-50 text-14 mb-1 italic'>
                Please choose the time when the message needs to play
              </Typography>
            </Stack>
          </Box>
          <List className='overflow-auto scrollbar w-300 h-100'>
            {dateRanges.map(val => (
              <ListItem key={val.id} disableGutters className='py-1'>
                <ListItemText
                  primaryTypographyProps={{className: 'text-14 pl-2'}}
                  primary={
                    <Stack
                      className='font-bold'
                      direction='row'
                      spacing={1}
                      alignContent='bottom'>
                      {val.start_date?.format('MM/DD/YYYY')} <TrendingFlat />{' '}
                      {val.end_date?.format('MM/DD/YYYY')}
                    </Stack>
                  }
                />
                {val.day_times &&
                (val.day_times.some(
                  val =>
                    val.selectedDays.length === 0 ||
                    val.timeRanges.length === 0,
                ) ||
                  val.isError ||
                  !isAllDaysTimed(val)) ? (
                  <img
                    src={IconWarning}
                    alt='Attention'
                    className='w-21 mr-2'
                    title='Empty Form'
                  />
                ) : (
                  <img
                    src={IconChecked}
                    alt='Attention'
                    className='w-21 mr-2'
                    title='Empty Form'
                  />
                )}
              </ListItem>
            ))}
          </List>
        </Stack>

        <Stack spacing={1} className='calcheight-100 w-full'>
          <Stack
            direction={'row'}
            alignItems='center'
            className='h-32'
            spacing={8}>
            <Stack className='font-bold' direction='row' spacing={1}>
              {dateRange.start_date?.format('MM/DD/YYYY')} <TrendingFlat />{' '}
              {dateRange.end_date?.format('MM/DD/YYYY')}
            </Stack>
            {!isAllDaysSelected ? (
              <Stack
                sx={{marginTop: 'auto'}}
                direction={'row'}
                justifyContent='flex-end'>
                <Button
                  startIcon={<AddIcon />}
                  variant='outlined'
                  size='small'
                  onClick={handleAddSlot}>
                  Add Slot
                </Button>
              </Stack>
            ) : null}
          </Stack>
          <Stack
            direction='row'
            spacing={2}
            className='pr-2 pb-2 calcheight-65 w-full overflow-x scrollbar'>
            {daysTimes &&
              daysTimes.map((item, index) => (
                <TimedMsgRequestTimesCard
                  key={item.id}
                  title={`Slot ${index + 1}`}
                  item={item}
                  enableWeekdays
                  selectedWeekdays={selectedWeekdays}
                  removeBtnDisabled={Boolean(
                    daysTimes && daysTimes.length === 1,
                  )}
                  setDaysTimes={setDaysTimes}
                  onRemove={handleRemove}
                  availableDays={availableDays}
                  duplicateTimes={duplicateTimes}
                  dateRange={dateRange}
                  dayType={dayType}
                />
              ))}
          </Stack>
          <Pagination
            count={dateRanges.length}
            page={page}
            onChange={handleChange}
          />
        </Stack>
      </Grid>

      {updateId && previousDaysTimes ? (
        <Grid item md={3}>
          <Typography className='text-20 font-bold' align='center'>
            Existing Time
          </Typography>
          <TimedMsgRequestReviewDayTimes items={previousDaysTimes} />
        </Grid>
      ) : null}
    </Grid>
  );
};
